'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { FindingCvssVersionKey } from '@terra/shared-cvss';
import {
  BodyStrong,
  HeadingSection,
  LabelDefault,
  MetaDefault,
  MonoDefault,
  MonoSmall,
} from '../../components/typography';
import {
  buildCvssVectorGroups,
  type CvssVectorMetric,
} from './finding-cvss-tab-metrics';
import { CVSS_TONE_STYLES } from './finding-cvss-tone-styles';

const TILE_MIN_WIDTH_PX = 120;

const LEGEND_TONES = ['raises', 'some', 'lowers'] as const;

const TONE_LABEL_KEYS = {
  raises: 'toneRaises',
  some: 'toneSome',
  lowers: 'toneLowers',
} as const;

function FindingCvssMetricColumn({ row }: { row: CvssVectorMetric }) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const styles = CVSS_TONE_STYLES[row.tone ?? 'unknown'];

  return (
    <VStack
      data-testid={`cvss-metric-${row.metric}`}
      gap={2}
      alignItems="stretch"
      minW={0}
    >
      <VStack
        gap={1}
        alignItems="flex-start"
        p={3}
        minW={0}
        overflowWrap="anywhere"
        borderRadius="md"
        borderWidth="1px"
        style={{ backgroundColor: styles.bg, borderColor: styles.border }}
      >
        <HStack gap={2} alignItems="baseline">
          <MonoSmall style={{ color: styles.fg }}>{row.metric}</MonoSmall>
          <MonoDefault fontWeight="bold" style={{ color: styles.fg }}>
            {row.value ?? '—'}
          </MonoDefault>
        </HStack>
        <BodyStrong>
          {row.value && row.tone
            ? t(`metricValues.${row.family}.${row.value}`)
            : (row.value ?? t('notScored'))}
        </BodyStrong>
        <MetaDefault color="fg.muted">
          {t(`metricNames.${row.metric}`)}
        </MetaDefault>
      </VStack>
      <Box
        borderLeftWidth="2px"
        pl={3}
        overflowWrap="anywhere"
        style={{ borderLeftColor: styles.border }}
      >
        <MetaDefault color={row.reason ? 'fg.default' : 'fg.muted'}>
          {row.reason ?? t('noReason')}
        </MetaDefault>
      </Box>
    </VStack>
  );
}

export type FindingCvssVectorStripProps = {
  version: FindingCvssVersionKey;
  vector: string | null;
  metricReasons: readonly { metric: string; reason: string }[];
};

export function FindingCvssVectorStrip({
  version,
  vector,
  metricReasons,
}: FindingCvssVectorStripProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const groups = buildCvssVectorGroups({ version, vector, metricReasons });

  return (
    <VStack gap={3} alignItems="stretch" w="full">
      <HStack justifyContent="space-between" flexWrap="wrap" gap={3}>
        <HeadingSection>{t('vectorTitle')}</HeadingSection>
        <HStack gap={3} flexWrap="wrap">
          {LEGEND_TONES.map((tone) => (
            <HStack key={tone} gap={1.5}>
              <Box
                w="2.5"
                h="2.5"
                borderRadius="sm"
                borderWidth="1px"
                style={{
                  backgroundColor: CVSS_TONE_STYLES[tone].bg,
                  borderColor: CVSS_TONE_STYLES[tone].border,
                }}
              />
              <MetaDefault color="fg.muted">
                {t(TONE_LABEL_KEYS[tone])}
              </MetaDefault>
            </HStack>
          ))}
        </HStack>
      </HStack>
      <HStack gap={6} alignItems="flex-start" flexWrap="wrap" w="full">
        {groups.map(({ group, metrics }) => (
          <VStack
            key={group}
            gap={2}
            alignItems="stretch"
            minW={0}
            style={{
              flexGrow: metrics.length,
              flexBasis: `${metrics.length * TILE_MIN_WIDTH_PX}px`,
            }}
          >
            <LabelDefault color="fg.muted">
              {t(`metricGroups.${group}`)}
            </LabelDefault>
            <Box
              display="grid"
              gap={2}
              alignItems="start"
              gridTemplateColumns="repeat(auto-fit, minmax(min(120px, 100%), 1fr))"
            >
              {metrics.map((row) => (
                <FindingCvssMetricColumn key={row.metric} row={row} />
              ))}
            </Box>
          </VStack>
        ))}
      </HStack>
      <MonoSmall color="fg.muted" wordBreak="break-all">
        {vector ?? '—'}
      </MonoSmall>
    </VStack>
  );
}
